/**
 * Management API — typed wrappers around `/v1/management/*`.
 *
 * Mirrors the OpenAPI shapes from `api/src/schemas/management.ts` +
 * `api/src/schemas/customer-dedup.ts`. Hand-redeclared so the module compiles
 * cleanly when the (gitignored) generated `types/api.ts` hasn't been refreshed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pujcovnaKeys } from '@/modules/pujcovna/api';
import { getAuthHeaders } from '@/lib/auth';
import type { components } from '@/types/api';

// ────────────────────────────────────────────────────────────────────────
// Brands / models
// ────────────────────────────────────────────────────────────────────────

export interface MgmtBrand {
  id: number;
  nazev: string;
}

interface BrandsResponse {
  brands: MgmtBrand[];
}

export function useMgmtBrandsQuery() {
  return useQuery({
    queryKey: ['management', 'brands'],
    queryFn: ({ signal }) =>
      api.get<BrandsResponse>('/v1/management/brands', { signal }),
  });
}

export interface MgmtModel {
  id: number;
  nazev_modelu: string;
}

interface ModelsResponse {
  models: MgmtModel[];
}

export function useMgmtModelsQuery(brandId: number | null) {
  return useQuery({
    queryKey: ['management', 'models', brandId],
    enabled: brandId != null && brandId > 0,
    queryFn: ({ signal }) =>
      api.get<ModelsResponse>(
        `/v1/management/models?znacka_vozu=${brandId}`,
        { signal },
      ),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Entities (Ceníky 10.2)
// ────────────────────────────────────────────────────────────────────────

export interface MgmtEntity {
  id: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

interface EntitiesResponse {
  entities: MgmtEntity[];
}

export function useMgmtEntitiesQuery(modelId: number | null) {
  return useQuery({
    queryKey: ['management', 'entities', modelId],
    enabled: modelId != null && modelId > 0,
    queryFn: ({ signal }) =>
      api.get<EntitiesResponse>(
        `/v1/management/entities?vozidlo_model=${modelId}`,
        { signal },
      ),
  });
}

export interface MgmtItem {
  id: number;
  id_entity: number;
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
  aktivni: number;
}

interface ColorsResponse {
  colors: MgmtItem[];
}
interface InteriorsResponse {
  interiors: MgmtItem[];
}
interface EquipmentResponse {
  equipment: MgmtItem[];
}

export function useMgmtColorsQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['management', 'colors', entityId],
    enabled: entityId != null && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<ColorsResponse>(`/v1/management/entities/${entityId}/colors`, {
        signal,
      }),
  });
}

export function useMgmtInteriorsQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['management', 'interiors', entityId],
    enabled: entityId != null && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<InteriorsResponse>(
        `/v1/management/entities/${entityId}/interiors`,
        { signal },
      ),
  });
}

export function useMgmtEquipmentQuery(entityId: number | null) {
  return useQuery({
    queryKey: ['management', 'equipment', entityId],
    enabled: entityId != null && entityId > 0,
    queryFn: ({ signal }) =>
      api.get<EquipmentResponse>(
        `/v1/management/entities/${entityId}/equipment`,
        { signal },
      ),
  });
}

export interface MgmtEntityBody {
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: number;
  nakupni_cena: number;
  /** When present, the API replaces (and deactivates) the referenced row. */
  id_entity?: number;
}

export interface MgmtItemBody {
  id_entity: number;
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
  id_barvy?: number;
  id_interieru?: number;
  id_vybavy?: number;
}

interface CreatedResponse {
  status: 'ok';
  id: number;
}

export function useCreateEntityMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MgmtEntityBody) =>
      api.post<CreatedResponse>('/v1/management/entities', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useDeleteEntityMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/management/entities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useCreateColorMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MgmtItemBody) =>
      api.post<CreatedResponse>('/v1/management/colors', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useDeleteColorMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/management/colors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useCreateInteriorMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MgmtItemBody) =>
      api.post<CreatedResponse>('/v1/management/interiors', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useDeleteInteriorMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/management/interiors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useCreateEquipmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MgmtItemBody) =>
      api.post<CreatedResponse>('/v1/management/equipment', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export function useDeleteEquipmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/management/equipment/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Ceník import (Nissan XLSX) — AI extraction + review commit. Mirrors the
// fleet-policy extract flow: NDJSON progress stream, then a reviewed draft
// is committed through one transactional endpoint.
// ────────────────────────────────────────────────────────────────────────

export interface CenikImportItemDraft {
  nazev: string;
  cenikova_cena: number;
  nakupni_cena: number;
}

export interface CenikImportEntityDraft {
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: number;
  nakupni_cena: number;
  rp_total: number | null;
  varovani: string[];
  barvy: CenikImportItemDraft[];
  interiery: CenikImportItemDraft[];
  vybavy: CenikImportItemDraft[];
}

export interface CenikImportModelDraft {
  list: string;
  model_kod: string;
  model_nazev: string;
  modelovy_rok: string | null;
  /** Server-side auto-match against vozidlo_modely; null = assign in review. */
  vozidlo_model: number | null;
  entity: CenikImportEntityDraft[];
}

export interface CenikImportDraft {
  znacka: number;
  zdrojKlic: string;
  zdrojNazev: string;
  modely: CenikImportModelDraft[];
  chyby: { list: string; chyba: string }[];
}

export interface CenikImportExtractProgress {
  /** ulozeno = workbook stored; cteni = AI reading a sheet; radky = entities streaming in. */
  faze: 'ulozeno' | 'cteni' | 'radky';
  /** Sheet currently being extracted + its position, once known. */
  nazev: string | null;
  poradi: number;
  celkem: number;
  pocetRadku: number;
}

export interface CenikImportExtractResponse {
  zdrojKlic: string;
  zdrojNazev: string;
  aiAvailable: boolean;
  draft: CenikImportDraft | null;
  chyba: string | null;
}

/**
 * The extract endpoint streams NDJSON progress events (one AI call per model
 * sheet, 10–20 minutes total): ulozeno → (list|prubeh|tep)* → hotovo|chyba.
 * Reads the stream, relays progress, resolves with the final draft.
 */
export function useCenikImportExtractMutation() {
  return useMutation({
    mutationFn: async (input: {
      file: File;
      onProgress?: (p: CenikImportExtractProgress) => void;
    }): Promise<CenikImportExtractResponse> => {
      const fd = new FormData();
      fd.append('file', input.file);
      const res = await fetch('/api/v1/management/cenik-import/extract', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { Accept: 'application/x-ndjson', ...getAuthHeaders() },
      });
      if (!res.ok || !res.body) {
        const text = await res.text();
        let msg = text || `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch {
          /* keep raw text */
        }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let saved: { zdrojKlic: string; zdrojNazev: string; aiAvailable: boolean } | null = null;
      let final: CenikImportExtractResponse | null = null;
      // Sheet position from the last 'list' event — merged into 'prubeh' updates.
      let pozice = { nazev: null as string | null, poradi: 0, celkem: 0 };

      const handleLine = (line: string) => {
        if (!line.trim()) return;
        const ev = JSON.parse(line) as {
          typ: 'ulozeno' | 'list' | 'prubeh' | 'tep' | 'hotovo' | 'chyba';
          zdrojKlic?: string;
          zdrojNazev?: string;
          aiAvailable?: boolean;
          nazev?: string;
          poradi?: number;
          celkem?: number;
          faze?: 'cteni' | 'radky';
          pocetRadku?: number;
          draft?: CenikImportDraft;
          chyba?: string;
        };
        switch (ev.typ) {
          case 'ulozeno':
            saved = { zdrojKlic: ev.zdrojKlic!, zdrojNazev: ev.zdrojNazev!, aiAvailable: ev.aiAvailable! };
            input.onProgress?.({ faze: 'ulozeno', ...pozice, pocetRadku: 0 });
            break;
          case 'list':
            pozice = { nazev: ev.nazev ?? null, poradi: ev.poradi ?? 0, celkem: ev.celkem ?? 0 };
            input.onProgress?.({ faze: 'cteni', ...pozice, pocetRadku: 0 });
            break;
          case 'prubeh':
            input.onProgress?.({ faze: ev.faze ?? 'cteni', ...pozice, pocetRadku: ev.pocetRadku ?? 0 });
            break;
          case 'hotovo':
            final = { ...saved!, draft: ev.draft ?? null, chyba: null };
            break;
          case 'chyba':
            final = { ...saved!, draft: null, chyba: ev.chyba ?? 'AI extrakce selhala' };
            break;
          case 'tep':
            break;
        }
      };

      // A mid-stream network failure surfaces as a read error — swallow it:
      // the workbook is already stored, the user gets a retry-friendly message.
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf('\n')) >= 0) {
            handleLine(buf.slice(0, nl));
            buf = buf.slice(nl + 1);
          }
        }
        handleLine(buf);
      } catch {
        /* fall through to the saved/interrupted handling below */
      }

      if (final) return final;
      if (saved) {
        const s = saved as { zdrojKlic: string; zdrojNazev: string; aiAvailable: boolean };
        return {
          ...s,
          draft: null,
          chyba: s.aiAvailable
            ? 'Spojení bylo přerušeno během vytěžování — sešit je uložen, spusťte import znovu'
            : null,
        };
      }
      throw new Error('Spojení se serverem bylo přerušeno — zkuste to znovu');
    },
  });
}

export interface CenikImportCommitEntity {
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: number;
  nakupni_cena: number;
  /** Active entity this row replaces; null = plain insert. */
  nahrazuje_id: number | null;
  barvy: CenikImportItemDraft[];
  interiery: CenikImportItemDraft[];
  vybavy: CenikImportItemDraft[];
}

export interface CenikImportCommitBody {
  zdroj_klic: string | null;
  zdroj_nazev: string | null;
  modely: {
    vozidlo_model: number;
    entity: CenikImportCommitEntity[];
    deaktivovat: number[];
  }[];
}

export interface CenikImportCommitResponse {
  status: 'ok';
  importId: number;
  modely: number;
  entityVlozeno: number;
  entityNahrazeno: number;
  entityDeaktivovano: number;
  barvy: number;
  interiery: number;
  vybavy: number;
  vybavyPreneseno: number;
}

export function useCenikImportCommitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CenikImportCommitBody) =>
      api.post<CenikImportCommitResponse>('/v1/management/cenik-import/commit', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management'] }),
  });
}

export interface CenikImportRecord {
  id: number;
  znacka: number;
  zdroj_nazev: string | null;
  /** JSON summary counts as stored by the commit. */
  souhrn: string | null;
  vytvoreno: string;
  vytvoril: number;
}

export function useCenikImportsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['management', 'cenik-import', 'list'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ imports: CenikImportRecord[] }>('/v1/management/cenik-import', { signal }),
  });
}

/** Download a past import's source workbook (bearer-authed → blob anchor). */
export async function openCenikImportZdroj(importId: number, nazev: string | null): Promise<void> {
  const res = await fetch(`/api/v1/management/cenik-import/${importId}/zdroj`, {
    credentials: 'same-origin',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Zdrojový sešit se nepodařilo stáhnout');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nazev ?? 'cenik.xlsx';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ────────────────────────────────────────────────────────────────────────
// Dedup zákazníků (10.3) — admin-only
// ────────────────────────────────────────────────────────────────────────

export type DedupMatchType = 'ico' | 'email' | 'manual';

export interface DedupChildCounts {
  vozidlo: number;
  vozidla_nabidka: number;
  vozidla_smlouva: number;
  ojete_vozidlo: number;
  ojeta_vozidla_nabidka: number;
  ojeta_vozidla_smlouva: number;
  ojeta_vozidla_vykup_smlouva: number;
  operak_nabidka: number;
  operak_smlouva: number;
  kontakt: number;
  servis_udalost: number;
  pneu_uskladneni_sada: number;
  pneu_vymena: number;
  total: number;
}

export interface DedupCustomer {
  id_zakaznika: number;
  jmeno: string;
  ulice: string;
  cislo_popisne: string;
  psc: string;
  mesto: string;
  ico: string;
  dico: string;
  zastupce: string;
  telefon: string;
  email: string;
  child_counts: DedupChildCounts;
}

export interface DedupConflict {
  field:
    | 'jmeno'
    | 'ulice'
    | 'cislo_popisne'
    | 'psc'
    | 'mesto'
    | 'ico'
    | 'dico'
    | 'zastupce'
    | 'telefon'
    | 'email';
  values: { id_zakaznika: number; value: string }[];
}

export interface DedupGroup {
  group_key: string;
  match_type: DedupMatchType;
  customers: DedupCustomer[];
  suggested_canonical_id: number;
  conflicts: DedupConflict[];
}

interface DedupGroupsResponse {
  groups: DedupGroup[];
}

export function useDedupGroupsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['management', 'dedup', 'groups'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<DedupGroupsResponse>('/v1/management/customers/dedup/groups', {
        signal,
      }),
  });
}

export interface DedupMergeBody {
  canonical_id: number;
  duplicate_ids: number[];
  field_overrides?: Record<string, string>;
}

interface DedupMergeResponse {
  status: 'ok';
  merge_id: string;
  repointed: DedupChildCounts;
  deleted: number;
}

export function useDedupMergeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DedupMergeBody) =>
      api.post<DedupMergeResponse>('/v1/management/customers/dedup/merge', body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['management', 'dedup'] }),
  });
}

export interface DedupLogEntry {
  id_log: number;
  merge_id: string;
  id_firmy: number;
  canonical_id: number;
  deleted_id: number;
  deleted_row: unknown;
  repoint_counts: unknown;
  field_overrides: unknown;
  id_uzivatele: number;
  datum: string;
}

interface DedupLogResponse {
  entries: DedupLogEntry[];
}

export function useDedupLogQuery(
  enabled: boolean = true,
  limit: number = 50,
) {
  return useQuery({
    queryKey: ['management', 'dedup', 'log', limit],
    enabled,
    queryFn: ({ signal }) =>
      api.get<DedupLogResponse>(
        `/v1/management/customers/dedup/log?limit=${limit}`,
        { signal },
      ),
  });
}

// ────────────────────────────────────────────────────────────────────────
// SPZ backfill from handover protocols — admin-only
// ────────────────────────────────────────────────────────────────────────

export interface SpzProposal {
  spz: string;
  id_smlouvy: number;
  id_protokolu: number;
  protokol_vin: string;
  vin_match: boolean;
  datum: string;
}

export interface SpzCandidate {
  id_vozidla: number;
  vc_auta: string;
  vin: string;
  znacka_nazev: string;
  model_nazev: string;
  provedeni: string;
  suggested_spz: string;
  conflict: boolean;
  proposals: SpzProposal[];
}

interface SpzCandidatesResponse {
  candidates: SpzCandidate[];
}

export function useSpzCandidatesQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['management', 'spz', 'candidates'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<SpzCandidatesResponse>('/v1/management/vehicles/spz/candidates', {
        signal,
      }),
  });
}

export interface SpzBackfillBody {
  pairs: { id_vozidla: number; spz: string }[];
}

export interface SpzBackfillResponse {
  status: 'ok';
  updated: number;
  skipped: { id_vozidla: number; reason: string }[];
}

export function useSpzBackfillMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpzBackfillBody) =>
      api.post<SpzBackfillResponse>('/v1/management/vehicles/spz/backfill', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'spz'] }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Users (10.4) — admin-only
// ────────────────────────────────────────────────────────────────────────

export interface MgmtUser {
  id_uzivatele: number;
  nick: string;
  jmeno: string;
  prijmeni: string;
  osloveni: string;
  email: string;
  admin: number;
  lms_id: number;
  barva: string;
  aktivni: number;
}

interface UsersListResponse {
  users: MgmtUser[];
  total: number;
}

interface UserDetailResponse {
  user: MgmtUser;
}

export interface UsersListParams {
  search?: string;
  aktivni?: 0 | 1;
  limit?: number;
  offset?: number;
}

export function useUsersListQuery(params: UsersListParams = {}, enabled: boolean = true) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.aktivni != null) qs.set('aktivni', String(params.aktivni));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return useQuery({
    queryKey: ['management', 'users', { search: params.search ?? '', aktivni: params.aktivni ?? null, limit: params.limit ?? null, offset: params.offset ?? null }],
    enabled,
    queryFn: ({ signal }) => api.get<UsersListResponse>(`/v1/users${q ? `?${q}` : ''}`, { signal }),
  });
}

export function useUserDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['management', 'users', 'detail', id],
    enabled: id != null && id > 0,
    // Backs the user edit form — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) => api.get<UserDetailResponse>(`/v1/users/${id}`, { signal }),
  });
}

export interface UserCreateBody {
  nick: string;
  /** 32-char lowercase hex MD5 of the plaintext password. */
  heslo: string;
  jmeno: string;
  prijmeni: string;
  osloveni?: string;
  email: string;
  admin?: 0 | 1;
  lms_id?: number;
  barva?: string;
}

export interface UserUpdateBody {
  nick?: string;
  heslo?: string;
  jmeno?: string;
  prijmeni?: string;
  osloveni?: string;
  email?: string;
  admin?: 0 | 1;
  lms_id?: number;
  barva?: string;
}

export function useCreateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateBody) => api.post<UserDetailResponse>('/v1/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'users'] }),
  });
}

export function useUpdateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserUpdateBody }) =>
      api.put<UserDetailResponse>(`/v1/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'users'] }),
  });
}

export function useDeleteUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'users'] }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Deactivation — soft archive + open-data reassignment
// ────────────────────────────────────────────────────────────────────────

export interface DeactivationPreflightFirma {
  id_firmy: number;
  nazev: string;
  kontakty: number;
  plany: number;
  ukoly: number;
  vyzaduje_nastupce: boolean;
}

interface DeactivationPreflightResponse {
  user: { id_uzivatele: number; aktivni: number };
  firmy: DeactivationPreflightFirma[];
}

export function useDeactivationPreflightQuery(userId: number | null) {
  return useQuery({
    queryKey: ['management', 'users', userId, 'deactivation-preflight'],
    enabled: userId != null && userId > 0,
    // Drives an irreversible-looking action — always show fresh counts.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<DeactivationPreflightResponse>(`/v1/users/${userId}/deactivation-preflight`, {
        signal,
      }),
  });
}

export interface DeactivateBody {
  nastupci: { id_firmy: number; id_uzivatele: number }[];
}

/** Deactivated users vanish from the seller/assignee pickers — drop those caches too. */
function invalidateUserPickerCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['management', 'users'] });
  qc.invalidateQueries({ queryKey: ['contacts', 'sellers'] });
  qc.invalidateQueries({ queryKey: ['tasks', 'users'] });
  qc.invalidateQueries({ queryKey: ['firma'] });
}

export function useDeactivateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DeactivateBody }) =>
      api.post<{ status: 'ok' }>(`/v1/users/${id}/deactivate`, body),
    onSuccess: () => invalidateUserPickerCaches(qc),
  });
}

export function useActivateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ status: 'ok' }>(`/v1/users/${id}/activate`, {}),
    onSuccess: () => invalidateUserPickerCaches(qc),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Memberships (shared by 10.4 and 10.5)
// ────────────────────────────────────────────────────────────────────────

export interface MgmtMembership {
  id: number;
  id_uzivatele: number;
  id_firmy: number;
  primarni: number;
  /** Populated on /v1/users/:id/companies. */
  Nazev?: string;
  /** /v1/users/:id/companies: assigned LMS location ids in that company. */
  lms_pobocky?: number[];
  /** /v1/users/:id/companies: the company's LMS locations (main first). */
  firma_lms_pobocky?: { id: number; nazev: string }[];
  /** Populated on /v1/companies/:id/users. */
  nick?: string;
  jmeno?: string;
  prijmeni?: string;
  email?: string;
  admin?: number;
  aktivni?: number;
}

interface MembershipListResponse {
  memberships: MgmtMembership[];
}

export function useUserMembershipsQuery(userId: number | null) {
  return useQuery({
    queryKey: ['management', 'users', userId, 'memberships'],
    enabled: userId != null && userId > 0,
    queryFn: ({ signal }) =>
      api.get<MembershipListResponse>(`/v1/users/${userId}/companies`, { signal }),
  });
}

export interface UserMembershipCreateBody {
  id_firmy: number;
  primarni?: 0 | 1;
}

export interface MembershipUpdateBody {
  primarni?: 0 | 1;
}

export function useAddUserMembershipMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: UserMembershipCreateBody }) =>
      api.post(`/v1/users/${userId}/companies`, body),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['management', 'users', vars.userId, 'memberships'] }),
  });
}

/**
 * Replace a member's LMS locations in one company ([] = no LMS lead access).
 * Platform-admin setting (Platforma → Uživatelé); company admins only see it
 * in Členové. Refreshes both views and /me (your own dashboard card).
 */
export function useUpdateMembershipLmsPobockyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, pobocky }: { userId: number; companyId: number; pobocky: number[] }) =>
      api.put<{ status: 'ok' }>(`/v1/users/${userId}/companies/${companyId}/lms-pobocky`, { pobocky }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['management', 'users', vars.userId, 'memberships'] });
      qc.invalidateQueries({ queryKey: ['firma'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useUpdateUserMembershipMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      body,
    }: {
      userId: number;
      companyId: number;
      body: MembershipUpdateBody;
    }) => api.put(`/v1/users/${userId}/companies/${companyId}`, body),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['management', 'users', vars.userId, 'memberships'] }),
  });
}

export function useRemoveUserMembershipMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId }: { userId: number; companyId: number }) =>
      api.delete(`/v1/users/${userId}/companies/${companyId}`),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['management', 'users', vars.userId, 'memberships'] }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Companies (10.5) — admin-only
// ────────────────────────────────────────────────────────────────────────

export interface MgmtCompany {
  id_firmy: number;
  Nazev: string;
  ulice: string;
  cp: string;
  mesto: string;
  psc: string;
  ICO: number;
  DIC: string;
  zapis_OR: string;
  banka: string;
  ucet: string;
  /** Corporate group (Platforma → Firmy → Skupiny); null = none. */
  id_skupiny: number | null;
  /** Enabled modules (access overhaul Stage 6) — replaces the 7 flag columns. */
  moduly: CompanyModul[];
  /** LMS dealer accounts, one per location; the first is the main one. */
  lms_pobocky: CompanyLmsPobocka[];
  /** Delivery places ("Místo předání" on offers/contracts), by name. */
  mista_predani: CompanyMistoPredani[];
}

export interface CompanyMistoPredani {
  id: number;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
}

/** A place in a create/update body; `id` = keep (update) that place. */
export interface CompanyMistoPredaniInput {
  id?: number;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
}

export interface CompanyLmsPobocka {
  id: number;
  nazev: string;
  /** Ford dealer ID (DID). */
  lms_id: number;
  lms_req_key: string;
  lms_res_key: string;
  poradi: number;
}

/** A location in a create/update body; `id` = keep (update) that location. */
export interface CompanyLmsPobockaInput {
  id?: number;
  nazev: string;
  lms_id: number;
  lms_req_key: string;
  lms_res_key: string;
}

export type CompanyModul =
  | 'kontakty'
  | 'vozidla'
  | 'ojeta_vozidla'
  | 'leasing'
  | 'servis'
  | 'uskladneni'
  | 'posta'
  | 'kalendar'
  | 'ukoly'
  | 'pujcovna';

interface CompaniesListResponse {
  companies: MgmtCompany[];
  total: number;
}

interface CompanyDetailResponse {
  company: MgmtCompany;
}

export interface CompaniesListParams {
  search?: string;
  limit?: number;
  offset?: number;
}

export function useCompaniesListQuery(
  params: CompaniesListParams = {},
  enabled: boolean = true,
) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return useQuery({
    queryKey: [
      'management',
      'companies',
      { search: params.search ?? '', limit: params.limit ?? null, offset: params.offset ?? null },
    ],
    enabled,
    queryFn: ({ signal }) =>
      api.get<CompaniesListResponse>(`/v1/companies${q ? `?${q}` : ''}`, { signal }),
  });
}

export function useCompanyDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['management', 'companies', 'detail', id],
    enabled: id != null && id > 0,
    // Backs the company edit form — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) => api.get<CompanyDetailResponse>(`/v1/companies/${id}`, { signal }),
  });
}

export interface CompanyCreateBody {
  Nazev: string;
  ulice?: string;
  cp?: string;
  mesto?: string;
  psc?: string;
  ICO?: number;
  DIC?: string;
  zapis_OR?: string;
  banka?: string;
  ucet?: string;
  /** Group to put the firma into; null removes it (409 while cross-firma dependencies exist). */
  id_skupiny?: number | null;
  moduly?: CompanyModul[];
  /** The complete location list: order = poradi, missing locations are deleted. */
  lms_pobocky?: CompanyLmsPobockaInput[];
  /** The complete place list: missing places are deleted (409 if still in use). */
  mista_predani?: CompanyMistoPredaniInput[];
}

export type CompanyUpdateBody = Partial<CompanyCreateBody>;

// ────────────────────────────────────────────────────────────────────────
// Firma groups — platform admin (skupina-vlastnictvi-plan S1)
// ────────────────────────────────────────────────────────────────────────

export interface MgmtSkupina {
  id: number;
  nazev: string;
  firmy: { id_firmy: number; Nazev: string }[];
}

const SKUPINY_KEY = ['management', 'skupiny'] as const;

export function useSkupinyQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: SKUPINY_KEY,
    enabled,
    queryFn: ({ signal }) => api.get<{ skupiny: MgmtSkupina[] }>('/v1/skupiny', { signal }),
  });
}

/** Groups change which firma sits where, so the company lists refresh with them. */
function invalidateSkupiny(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: SKUPINY_KEY });
  void qc.invalidateQueries({ queryKey: ['management', 'companies'] });
}

export function useCreateSkupinaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { nazev: string }) => api.post<{ skupina: MgmtSkupina }>('/v1/skupiny', body),
    onSuccess: () => invalidateSkupiny(qc),
  });
}

export function useRenameSkupinaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nazev }: { id: number; nazev: string }) =>
      api.put<{ skupina: MgmtSkupina }>(`/v1/skupiny/${id}`, { nazev }),
    onSuccess: () => invalidateSkupiny(qc),
  });
}

export function useDeleteSkupinaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/v1/skupiny/${id}`),
    onSuccess: () => invalidateSkupiny(qc),
  });
}

export function useCreateCompanyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompanyCreateBody) =>
      api.post<CompanyDetailResponse>('/v1/companies', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['management', 'companies'] });
      // A firma can be created straight into a group — the groups' member lists change with it.
      void qc.invalidateQueries({ queryKey: SKUPINY_KEY });
    },
  });
}

export function useUpdateCompanyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: CompanyUpdateBody }) =>
      api.put<CompanyDetailResponse>(`/v1/companies/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['management', 'companies'] });
      // Moving a firma between groups changes the groups' member lists (and which ones are deletable).
      void qc.invalidateQueries({ queryKey: SKUPINY_KEY });
      // The offer/contract "Místo předání" dropdown reads the same places.
      void qc.invalidateQueries({ queryKey: ['reference', 'delivery-places'] });
    },
  });
}

// ==================== Company Slack config ====================

export interface CompanySlackStatus {
  slack_enabled: number;
  slack_configured: boolean;
}

export function useCompanySlackQuery(id: number | null) {
  return useQuery({
    queryKey: ['management', 'companies', id, 'slack'],
    enabled: id != null && Number.isFinite(id),
    queryFn: ({ signal }) => api.get<CompanySlackStatus>(`/v1/companies/${id}/slack`, { signal }),
  });
}

export function useUpdateCompanySlackMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slack_bot_token?: string; slack_enabled?: number }) =>
      api.put<CompanySlackStatus>(`/v1/companies/${id}/slack`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'companies', id, 'slack'] }),
  });
}

export function useTestCompanySlackMutation(id: number) {
  return useMutation({
    mutationFn: () => api.post<{ status: 'ok' }>(`/v1/companies/${id}/slack/test`),
  });
}

export interface CompanyDeleteBlocked {
  error: string;
  blocking: Record<string, number>;
}

export interface CompanyDeleteResponse {
  status: 'ok';
  /** Present only on the force-delete path: per-table deleted row counts. */
  smazano?: Record<string, number>;
}

export function useDeleteCompanyMutation() {
  const qc = useQueryClient();
  return useMutation({
    // `potvrzeni` (the exact company Nazev) switches the API to force-delete:
    // the company is removed INCLUDING all its data.
    mutationFn: ({ id, potvrzeni }: { id: number; potvrzeni?: string }) =>
      api.delete<CompanyDeleteResponse>(
        `/v1/companies/${id}`,
        potvrzeni !== undefined ? { body: { potvrzeni } } : undefined,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['management', 'companies'] });
      // A deleted firma leaves its group.
      void qc.invalidateQueries({ queryKey: SKUPINY_KEY });
    },
  });
}

// Company memberships — list users + assign / update / remove
export function useCompanyMembersQuery(companyId: number | null) {
  return useQuery({
    queryKey: ['management', 'companies', companyId, 'members'],
    enabled: companyId != null && companyId > 0,
    queryFn: ({ signal }) =>
      api.get<MembershipListResponse>(`/v1/companies/${companyId}/users`, { signal }),
  });
}

export interface CompanyMembershipCreateBody {
  id_uzivatele: number;
  primarni?: 0 | 1;
}

export function useAddCompanyMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: number; body: CompanyMembershipCreateBody }) =>
      api.post(`/v1/companies/${companyId}/users`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['management', 'companies', vars.companyId, 'members'] });
      qc.invalidateQueries({ queryKey: ['management', 'users'] });
    },
  });
}

export function useUpdateCompanyMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      userId,
      body,
    }: {
      companyId: number;
      userId: number;
      body: MembershipUpdateBody;
    }) => api.put(`/v1/companies/${companyId}/users/${userId}`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['management', 'companies', vars.companyId, 'members'] });
      qc.invalidateQueries({ queryKey: ['management', 'users'] });
    },
  });
}

export function useRemoveCompanyMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, userId }: { companyId: number; userId: number }) =>
      api.delete(`/v1/companies/${companyId}/users/${userId}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['management', 'companies', vars.companyId, 'members'] });
      qc.invalidateQueries({ queryKey: ['management', 'users'] });
    },
  });
}

// ────────────────────────────────────────────────────────────────────────
// LMS catalog (10.6) — admin-targeted; mutations only trigger explicit
// reads of large, expensive HMAC-signed payloads from the LMS proxy.
// ────────────────────────────────────────────────────────────────────────

/** A row from the LMS-side masterdata models call. */
export interface LmsModel extends Record<string, unknown> {
  ID?: number | string;
  LMS_ID?: number | string;
  Nazev?: string;
  Znacka?: string;
  Model?: string;
}

/** A row from the local DB models JOIN. */
export interface MgmtDbModel {
  id: number;
  lms_id: number;
  nazev_modelu: string;
}

interface LmsCompareModelsResponse {
  lms_models: LmsModel[];
  db_models: MgmtDbModel[];
}

export function useLmsCompareModelsMutation() {
  return useMutation({
    mutationFn: () => {
      const now = new Date();
      const ts = now.toISOString();
      const back = new Date(now);
      back.setDate(back.getDate() - 10);
      const datum = back.toISOString();
      return api.post<LmsCompareModelsResponse>('/v1/management/lms/compare-models', {
        ts,
        datum,
      });
    },
  });
}

/** LMS stock vehicle row — schema is wide-open, kept as record. */
export type LmsStockVehicle = Record<string, unknown>;

interface LmsStockResponse {
  stock_vehicles: LmsStockVehicle[];
}

export function useLmsStockVehiclesMutation() {
  return useMutation({
    mutationFn: () => {
      const ts = new Date().toISOString();
      return api.get<LmsStockResponse>(
        `/v1/management/lms/stock-vehicles?ts=${encodeURIComponent(ts)}`,
      );
    },
  });
}

export interface LmsDbCar extends Record<string, unknown> {
  id_vozidla: number;
  vc_auta: string;
  vin: string;
  stav_vozidla: string;
  znacka_nazev?: string;
  model_nazev?: string;
  provedeni?: string;
  hodnota_faktury?: string | number | null;
  datum_faktury?: string | null;
  datum_posledni_zmeny?: string | null;
}

export interface LmsExternalCar extends Record<string, unknown> {
  InterniCisloVozu?: string;
  VIN?: string;
  Model?: string;
  Znacka?: string;
  CisloObjednavky?: string;
  DatumPosledniZmeny?: string;
  SEERStatus?: string;
  StatusCombined?: string;
  Smlouva?: boolean | number | string;
}

export interface LmsFieldDifference {
  database: string;
  external: string;
  field_label: string;
}

export interface LmsComparisonItem {
  db_car: LmsDbCar;
  external_car: LmsExternalCar;
  differences: Record<string, LmsFieldDifference>;
}

export interface LmsCompareStockResponse {
  outdated: LmsComparisonItem[];
  upToDateWithDifferences: LmsComparisonItem[];
  completelyEqual: LmsComparisonItem[];
  unmatchedExternal: LmsExternalCar[];
  factoryOnlyButInDb: { external_car: LmsExternalCar; db_car: LmsDbCar }[];
}

export function useLmsCompareStockMutation() {
  return useMutation({
    mutationFn: () => {
      const ts = new Date().toISOString();
      return api.post<LmsCompareStockResponse>('/v1/management/lms/compare-stock', { ts });
    },
  });
}

export type LmsPrevzitBody = components['schemas']['MgmtLmsPrevzitBody'];
export type LmsPrevzitResponse = components['schemas']['MgmtLmsPrevzitResponse'];

/**
 * Take over an LMS-only stock car into the local database ("Převzít" on the
 * "Pouze v LMS" rows). The server re-reads the stock and the catalog itself.
 */
export function useLmsPrevzitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LmsPrevzitBody) =>
      api.post<LmsPrevzitResponse>('/v1/management/lms/prevzit', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

export interface VehiclePatchBody {
  field: string;
  value: string;
  external_date?: string;
}

interface VehiclePatchResponse {
  success: boolean;
  message: string;
}

export function useVehiclePatchMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: VehiclePatchBody }) =>
      api.patch<VehiclePatchResponse>(`/v1/management/vehicles/${id}`, body),
    // The field edit runs through the stock guard like the edit forms do (VIN / state change the rental entry).
    onSuccess: () => qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// LMS catalog explorer (debug) — unfiltered drill-down through the cenikový
// katalog. Mirrors the production `/v1/vehicles/lms/*` cascade but hits the
// management endpoints that skip the picker's dedup / 10-item cap so admins
// see everything the factory returns.
// ────────────────────────────────────────────────────────────────────────

export type LmsCatalogRecord = Record<string, unknown>;

export function useLmsCatalogModelsQuery(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['management', 'lms', 'catalog', 'models'],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ models: LmsCatalogRecord[] }>('/v1/management/lms/catalog/models', {}, { signal }),
  });
}

export function useLmsCatalogModelYearsQuery(modelId: number | string | null) {
  return useQuery({
    enabled: modelId != null && modelId !== '',
    queryKey: ['management', 'lms', 'catalog', 'model-years', modelId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ modelYears: LmsCatalogRecord[] }>(
        '/v1/management/lms/catalog/model-years',
        { id_modelu: modelId },
        { signal },
      ),
  });
}

export function useLmsCatalogPricelistsQuery(
  modelId: number | string | null,
  modelCode: string | null,
) {
  return useQuery({
    enabled: modelId != null && modelId !== '' && !!modelCode,
    queryKey: ['management', 'lms', 'catalog', 'pricelists', modelId, modelCode],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ pricelists: LmsCatalogRecord[] }>(
        '/v1/management/lms/catalog/pricelists',
        { id_modelu: modelId, modelID: modelCode },
        { signal },
      ),
  });
}

export function useLmsCatalogEntitiesQuery(pricelistId: number | string | null) {
  return useQuery({
    enabled: pricelistId != null && pricelistId !== '',
    queryKey: ['management', 'lms', 'catalog', 'entities', pricelistId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ entities: LmsCatalogRecord[] }>(
        '/v1/management/lms/catalog/entities',
        { id_ceniku: pricelistId },
        { signal },
      ),
  });
}

export function useLmsCatalogEquipmentQuery(entityId: number | string | null) {
  return useQuery({
    enabled: entityId != null && entityId !== '' && entityId !== 0,
    queryKey: ['management', 'lms', 'catalog', 'equipment', entityId],
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.post<{ equipment: LmsCatalogRecord }>(
        '/v1/management/lms/catalog/equipment',
        { id_entity: entityId },
        { signal },
      ),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Firma — members + grants (access overhaul Stage 5). Spravce-gated
// server-side (requireSpravce); typed straight off the generated spec.
// ────────────────────────────────────────────────────────────────────────

export type FirmaMember = components['schemas']['FirmaMember'];
export type FirmaMembersResponse = components['schemas']['FirmaMembersResponse'];
export type FirmaUroven = components['schemas']['Uroven'];

/** Members of the active company + the company's entitled modules. */
export function useFirmaMembersQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['firma', 'members'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<FirmaMembersResponse>('/v1/firma/members', { signal }),
  });
}

export interface FirmaServisEmail {
  nazev: string;
  telefon: string;
  kontakt: string;
  /** Emails only go out once name + contact e-mail are both set. */
  aktivni: boolean;
}

/** The company's servis customer-email identity (signature, footer, reply-to). */
export function useFirmaServisEmailQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['firma', 'servis-email'],
    enabled,
    queryFn: ({ signal }) => api.get<FirmaServisEmail>('/v1/firma/servis-email', { signal }),
  });
}

export interface FirmaNabidky {
  /** Days a price offer stays valid (printed as "Platnost nabídky"). */
  platnost_dni: number;
}

/** The company's price-offer settings (validity in days). */
export function useFirmaNabidkyQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['firma', 'nabidky'],
    enabled,
    queryFn: ({ signal }) => api.get<FirmaNabidky>('/v1/firma/nabidky', { signal }),
  });
}

export function useUpdateFirmaNabidkyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FirmaNabidky) => api.put<FirmaNabidky>('/v1/firma/nabidky', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firma', 'nabidky'] });
      // Printouts read the validity off the offer summary.
      qc.invalidateQueries({ queryKey: ['vehicles', 'nabidky', 'summary'] });
      qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka', 'summary'] });
      qc.invalidateQueries({ queryKey: ['historie'] });
    },
  });
}

export function useUpdateFirmaServisEmailMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Pick<FirmaServisEmail, 'nazev' | 'telefon' | 'kontakt'>) =>
      api.put<FirmaServisEmail>('/v1/firma/servis-email', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firma', 'servis-email'] });
      qc.invalidateQueries({ queryKey: ['historie'] });
    },
  });
}

/** Set one member's direct grant(s) — `'none'` deletes the grant. */
export function useUpdatePravaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, prava }: { userId: number; prava: Record<string, FirmaUroven> }) =>
      api.put<{ status: 'ok' }>(`/v1/firma/members/${userId}/prava`, { prava }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['firma'] }),
  });
}

/** Toggle a member's company-admin flag (self-demotion is a server 409). */
export function useUpdateSpravceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, spravce }: { userId: number; spravce: 0 | 1 }) =>
      api.put<{ status: 'ok' }>(`/v1/firma/members/${userId}/spravce`, { spravce }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['firma'] }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Auth / admin gate
// ────────────────────────────────────────────────────────────────────────

interface AuthMeResponse {
  user: {
    id: number;
    nick: string;
    jmeno: string;
    prijmeni: string;
    email: string;
    admin: boolean;
  };
  company_id: number;
  membership: unknown;
}

export function useAuthMeQuery() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => api.get<AuthMeResponse>('/v1/auth/me', { signal }),
    staleTime: 60_000,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Cron job run log (admin only)
// ────────────────────────────────────────────────────────────────────────

export interface CronLog {
  id: number;
  job: string;
  status: 'running' | 'ok' | 'error';
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  summary: string | null;
  error: string | null;
}

interface CronLogsResponse {
  logs: CronLog[];
}

export function useCronLogsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['management', 'cron-logs'],
    queryFn: ({ signal }) => api.get<CronLogsResponse>('/v1/management/cron-logs', { signal }),
    enabled,
    refetchInterval: 30_000,
  });
}

export interface DigestRunResult {
  status: 'ok';
  companies: number;
  recipients: number;
  sent: number;
  errors: number;
}

export interface DigestTestResult {
  status: 'ok';
  sent: boolean;
  hadItems: boolean;
  reason: string | null;
}

/** Run the real daily digest now for everyone (admin). Refreshes the log. */
export function useRunDailyDigestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DigestRunResult>('/v1/management/cron/daily-digest/run'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'cron-logs'] }),
  });
}

/** Send the digest to the current admin only — safe self-test. */
export function useTestDailyDigestMutation() {
  return useMutation({
    mutationFn: () => api.post<DigestTestResult>('/v1/management/cron/daily-digest/test'),
  });
}

// ────────────────────────────────────────────────────────────────────────
// EBM (Ford) reporting — admin-only. Versioned account→OZN mapping plus
// monthly výkaz upload + diff. Mirrors api/src/schemas/management.ts EBM block.
// ────────────────────────────────────────────────────────────────────────

export interface EbmRadek {
  ucet: string;
  nazev: string | null;
  ozn: string;
  /** The report sums |netto| for this účet instead of netto. */
  absolutni: boolean;
}

export interface EbmVerze {
  id: number;
  verze: number;
  poznamka: string | null;
  zdroj_import_id: number | null;
  vytvoreno: string;
  pocet: number;
}

export interface EbmVysledovkaResponse {
  version: EbmVerze | null;
  rows: EbmRadek[];
  versions: EbmVerze[];
}

export interface EbmNewAccount {
  ucet: string;
  nazev: string;
}

export interface EbmNameBackfill {
  ucet: string;
  nazev: string;
}

export interface EbmVykazUploadResponse {
  importId: number;
  rok: number;
  mesic: number;
  pocetUctu: number;
  matched: number;
  newAccounts: EbmNewAccount[];
  nameBackfills: EbmNameBackfill[];
}

export interface EbmMappingInput {
  ucet: string;
  nazev?: string;
  ozn: string;
  absolutni?: boolean;
}

export interface EbmNameUpdateInput {
  ucet: string;
  nazev: string;
}

export interface EbmOznUpdateInput {
  ucet: string;
  ozn: string;
}

export interface EbmAbsUpdateInput {
  ucet: string;
  absolutni: boolean;
}

export interface EbmSaveMappingsResponse {
  status: 'ok';
  verzeId: number;
  verze: number;
  pocet: number;
  pridano: number;
  nazvyDoplneno: number;
  oznZmeneno: number;
  absZmeneno: number;
}

/** Current (or a specific) výsledovka version + rows + version history. */
/** EBM report type (mapping is versioned per type). */
export type EbmTyp = 'vykaz' | 'rozvaha_aktiva' | 'rozvaha_pasiva';

export interface EbmBaselineResponse {
  status: 'ok';
  verzeId: number;
  verze: number;
  pocet: number;
}

export function useEbmVysledovkaQuery(enabled: boolean, typ: EbmTyp, verze?: number) {
  return useQuery({
    queryKey: ['management', 'ebm', 'vysledovka', typ, verze ?? 'latest'],
    enabled,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ typ });
      if (verze != null) params.set('verze', String(verze));
      return api.get<EbmVysledovkaResponse>(`/v1/management/ebm/vysledovka?${params.toString()}`, { signal });
    },
  });
}

/** Upload the baseline mapping (.xlsx) as version 1 for a report type. */
export function useEbmBaselineUploadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; typ: EbmTyp }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('typ', input.typ);
      const headers: Record<string, string> = { Accept: 'application/json', ...getAuthHeaders() };
      const res = await fetch('/api/v1/management/ebm/baseline', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = text || `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch {
          /* keep raw text */
        }
        throw new Error(msg);
      }
      return (await res.json()) as EbmBaselineResponse;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'ebm'] }),
  });
}

/** Upload a monthly export (multipart) — persists it and returns unmapped účet. */
export function useEbmVykazUploadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; typ: EbmTyp; rok: number; mesic: number }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('typ', input.typ);
      fd.append('rok', String(input.rok));
      fd.append('mesic', String(input.mesic));
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...getAuthHeaders(),
      };
      const res = await fetch('/api/v1/management/ebm/vykaz', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = text || `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (parsed.error) msg = parsed.error;
        } catch {
          /* keep raw text */
        }
        throw new Error(msg);
      }
      return (await res.json()) as EbmVykazUploadResponse;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'ebm'] }),
  });
}

/** Save new mappings and/or confirmed name backfills, creating the next version. */
export function useEbmSaveMappingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      typ: EbmTyp;
      importId?: number;
      poznamka?: string;
      mappings?: EbmMappingInput[];
      nameUpdates?: EbmNameUpdateInput[];
      oznUpdates?: EbmOznUpdateInput[];
      absUpdates?: EbmAbsUpdateInput[];
    }) => api.post<EbmSaveMappingsResponse>('/v1/management/ebm/vysledovka', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'ebm'] }),
  });
}

// --- Report (per monthly import) ---

export interface EbmImport {
  id: number;
  rok: number;
  mesic: number;
  nazevSouboru: string | null;
  pocetUctu: number;
  pocetNovych: number;
  nahrano: string;
}

export interface EbmReportUcet {
  ucet: string;
  nazev: string | null;
  ozn: string;
  side: 'N' | 'V';
  /** Already |netto| when absolutni — the value that entered the sum. */
  netto: number;
  absolutni: boolean;
}

/** Which column a rule moves / reads. Empty for share rules and for the rozvaha. */
export type EbmSloupec = '' | 'N' | 'V';

/** A rule's effect on one line — negative on the source, positive on the target. */
export interface EbmPresun {
  smer: 'out' | 'in';
  /** The other cell: where the money went ('out') or came from ('in'). */
  ozn: string;
  druh: 'procento' | 'castka';
  procentoN: number;
  procentoV: number;
  /** Amount rules: the column moved, and the cell/column that set the amount. */
  sloupec: EbmSloupec;
  odkazOzn: string;
  odkazSloupec: EbmSloupec;
  naklady: number;
  nakladyZaporne: number;
  vynosy: number;
}

export interface EbmReportLine {
  base: string;
  /** Post-split: účty + presuny = this value. */
  naklady: number;
  nakladyZaporne: number;
  vynosy: number;
  ucty: EbmReportUcet[];
  presuny: EbmPresun[];
}

export interface EbmReportUnmapped {
  ucet: string;
  nazev: string | null;
  netto: number;
  ozn: string | null;
}

export interface EbmRozvahaPresun {
  smer: 'out' | 'in';
  ozn: string;
  druh: 'procento' | 'castka';
  procento: number;
  odkazOzn: string;
  hodnota: number;
}

export interface EbmRozvahaLine {
  ozn: string;
  hodnota: number;
  ucty: { ucet: string; nazev: string | null; netto: number; absolutni: boolean }[];
  presuny: EbmRozvahaPresun[];
}

export interface EbmReportResponse {
  import: { id: number; rok: number; mesic: number; nazevSouboru: string | null; nahrano: string };
  typ: EbmTyp;
  // Výkaz (P&L) — populated for typ='vykaz'.
  lines: EbmReportLine[];
  totals: { naklady: number; nakladyZaporne: number; vynosy: number };
  // Rozvaha (balance sheet) — populated for rozvaha types, else null.
  rozvaha: { lines: EbmRozvahaLine[]; total: number } | null;
  unmapped: EbmReportUnmapped[];
}

/** Uploaded monthly imports for a report type — the report's month picker. */
export function useEbmImportsQuery(enabled: boolean, typ: EbmTyp) {
  return useQuery({
    queryKey: ['management', 'ebm', 'imports', typ],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ imports: EbmImport[] }>(`/v1/management/ebm/imports?typ=${typ}`, { signal }),
  });
}

/** Computed EBM report for one uploaded import (výkaz N/V or rozvaha per cell). */
/**
 * One transfer between two cells.
 *
 * `procento` — the target receives a share; the source keeps 100 − Σ of its
 * targets. `castka` — the target receives, in column `sloupec`, an amount equal
 * to column `odkazSloupec` of cell `odkazOzn` (empty = the target itself).
 */
export interface EbmRozdeleniRadek {
  zdroj: string;
  cil: string;
  druh: 'procento' | 'castka';
  /** Výkaz: share of Náklady. Rozvaha: the single value's share. */
  procentoN: number;
  /** Výkaz: share of Výnosy. Unused for rozvaha. */
  procentoV: number;
  sloupec: EbmSloupec;
  odkazOzn: string;
  odkazSloupec: EbmSloupec;
}

export interface EbmRozdeleniResponse {
  version: EbmVerze | null;
  rules: EbmRozdeleniRadek[];
  versions: EbmVerze[];
}

export interface EbmSaveRozdeleniResponse {
  status: 'ok';
  verzeId: number;
  verze: number;
  pocet: number;
}

/** Current split rules for a report type + their version history. */
export function useEbmRozdeleniQuery(enabled: boolean, typ: EbmTyp) {
  return useQuery({
    queryKey: ['management', 'ebm', 'rozdeleni', typ],
    enabled,
    queryFn: ({ signal }) =>
      api.get<EbmRozdeleniResponse>(`/v1/management/ebm/rozdeleni?typ=${typ}`, { signal }),
  });
}

/** Replace the split rules with a new version (the whole set is sent). */
export function useEbmSaveRozdeleniMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { typ: EbmTyp; poznamka?: string; rules: EbmRozdeleniRadek[] }) =>
      api.post<EbmSaveRozdeleniResponse>('/v1/management/ebm/rozdeleni', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management', 'ebm'] }),
  });
}

export function useEbmReportQuery(importId: number | undefined, typ: EbmTyp, enabled: boolean) {
  return useQuery({
    queryKey: ['management', 'ebm', 'report', typ, importId],
    enabled: enabled && importId != null,
    queryFn: ({ signal }) =>
      api.get<EbmReportResponse>(`/v1/management/ebm/report?typ=${typ}&importId=${importId}`, { signal }),
  });
}

// ────────────────────────────────────────────────────────────────────────
// Premier (ApiComPrem) — read-only partner lookup
// ────────────────────────────────────────────────────────────────────────

export interface PremierPartner {
  inter: number;
  cislo: string;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
  ico: string;
  dic: string;
  tel: string;
  mobil: string;
  email: string;
  odberatel: boolean;
  dodavatel: boolean;
}

export interface PremierPartneriResponse {
  partneri: PremierPartner[];
  total: number;
  warning: string | null;
}

export interface PremierStatus {
  configured: boolean;
  connected: boolean | null;
  error: string | null;
}

/** Connection status probe — cheap, but hits the on-prem server; no auto-refresh. */
export function usePremierStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['management', 'premier', 'status'],
    enabled,
    staleTime: 60_000,
    queryFn: ({ signal }) => api.get<PremierStatus>('/v1/premier/status', { signal }),
  });
}

/** Partner search (debounced term; min 2 chars enforced by the caller). */
export function usePremierPartneriQuery(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['management', 'premier', 'partneri', term],
    enabled: enabled && term.trim().length >= 2,
    staleTime: 60_000,
    queryFn: ({ signal }) =>
      api.get<PremierPartneriResponse>(
        `/v1/premier/partneri?q=${encodeURIComponent(term.trim())}`,
        { signal },
      ),
  });
}

// ---------- Edge diagnostics (Platforma → Diagnostika) ----------

export type EdgeDiagnostics = components['schemas']['EdgeDiagnosticsResponse'];

/** The network path of THIS request as the API receives it. Always fetched fresh: it describes the moment. */
export function useEdgeDiagnosticsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['management', 'diagnostics', 'request'],
    queryFn: ({ signal }) => api.get<EdgeDiagnostics>('/v1/management/diagnostics/request', { signal }),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}
